import {
  RichUtils,
  EditorState,
  ContentBlock,
  ContentState,
  getVisibleSelectionRect,
  SelectionState,
  AtomicBlockUtils,
  Entity,
} from 'draft-js';
import { useInput } from 'lib/hooks/use-input';
import { useHasClickedOutside } from 'lib/hooks/use-has-clicked-outside';
import {
  BaseSyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { constants, getEntityIfSelected } from '../editor-utils';

export const videoStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === constants.EntityTypes.LINK
    );
  }, callback);
};

interface Props {
  contentState: ContentState;
  entityKey: string;
  children: any;
}

export const Video = ({ contentState, entityKey, children }: Props) => {
  const { url, alt } = contentState.getEntity(entityKey).getData();

  return <img className='' src={url} alt={alt} title={alt} />;
};

interface VideoComponentProps {
  editorState: EditorState;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
}
export const VideoComponent = ({
  editorState,
  setEditorState,
}: VideoComponentProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const videoUrl = useInput<string>('');
  const videoAlt = useInput<string>('');
  const ref = useRef(null);
  const hasClickedOutside = useHasClickedOutside(ref);
  const alreadyHasVideo = getEntityIfSelected(
    constants.BlockTypes.VIDEO,
    editorState
  );

  useEffect(() => {
    if (showModal && hasClickedOutside) {
      setShowModal(false);
      videoUrl.init('');
      videoAlt.init('');
    }
  });

  const handleCreateLink = () => {
    createVideo(editorState, setEditorState, videoUrl.value, videoAlt.value);
    setShowModal(false);
    videoUrl.init('');
    videoAlt.init('');
  };

  return (
    <>
      <div className='relative flex'>
        <button
          title='video'
          aria-label='video'
          className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded disabled:text-gray-400'
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          disabled={!!alreadyHasVideo}>
          <svg
            className='w-5 h-8'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
            />
          </svg>
        </button>
        {showModal && (
          <div
            ref={ref}
            className='z-10  absolute right-0 mt-8 rounded-md  ring-1 ring-black ring-opacity-5 w-72 [ bg-white border border-gray-100 shadow-lg sm:rounded-lg px-1 py-2 sm:px-2 flex flex-row flex-wrap ] space-y-2 '>
            <label
              htmlFor='src'
              className=' whitespace-nowrap my-auto text-sm font-medium text-gray-700 w-full flex flex-col'>
              Video Src
              <input
                {...videoUrl.bind}
                type='url'
                name='src'
                id='src'
                className='focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md sm:text-sm border-gray-300 min-w-full'
                placeholder='www.url.com'
                autoComplete='off'
              />
            </label>
            <label
              htmlFor='alt'
              className=' whitespace-nowrap my-auto text-sm font-medium text-gray-700 w-full flex flex-col'>
              Alt Text
              <input
                {...videoAlt.bind}
                type='text'
                name='alt'
                id='alt'
                className='focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md sm:text-sm border-gray-300 min-w-full'
                placeholder='What does the video represent'
                autoComplete='off'
              />
            </label>

            <button
              className='-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed disabled:text-gray-500'
              onClick={handleCreateLink}
              disabled={!videoUrl.isModified || !videoAlt.isModified}>
              <span>Embed</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export const createVideo = (
  state: EditorState,
  setState: Dispatch<SetStateAction<EditorState>>,
  videoUrl: string,
  videoAlt: string
) => {
  const contentState = state.getCurrentContent();

  const contentStateWithEntity = contentState.createEntity(
    constants.BlockTypes.VIDEO,
    'IMMUTABLE',
    {
      src: videoUrl,
      alt: videoAlt,
    }
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  const newEditorState = AtomicBlockUtils.insertAtomicBlock(
    state,
    entityKey,
    ' '
  );

  setState(newEditorState);
};

interface VideoBlockProps {
  src: string;
  alt: string;
}
export const VideoBlock = (props: VideoBlockProps) => {
  return <div dangerouslySetInnerHTML={{ __html: props.src }}></div>;
};

export const VideoPlugin = {
  strategy: videoStrategy,
  component: Video,
};
